/**
 * Theory of Sovereign Reflectivity
 * Theory Document Animations and Interactions
 */

// Use the global p5 variable from the CDN
// Create a global variable to track if the module has been initialized
let isInitialized = false;

// Safely initialize the module
function safeInitialize() {
    if (isInitialized) return;
    isInitialized = true;

    // Check if p5 is available from the CDN
    if (typeof p5 === 'undefined') {
        console.error('p5.js is not available. Make sure it is loaded before this script.');
        return;
    }

    try {
        // Initialize particle animation for the theory hero section
        if (document.getElementById('particles-js')) {
            initParticleAnimation();
        }

        // Initialize interactive diagrams
        const diagrams = document.querySelectorAll('.interactive-diagram');
        if (diagrams && diagrams.length > 0) {
            diagrams.forEach(diagram => {
                try {
                    const type = diagram.getAttribute('data-diagram-type');
                    if (type) {
                        initDiagram(diagram, type);
                    }
                } catch (err) {
                    console.warn(`Failed to initialize diagram: ${err.message}`);
                }
            });
        }

        // Initialize reading progress bar
        initReadingProgress();

        // Initialize reading settings
        initReadingSettings();

        // Initialize bookmarks
        initBookmarks();

        // Initialize highlight tooltips
        initHighlightTooltips();

        // Add a tooltip to explain interactive elements on first visit
        addInteractiveElementsTooltip();
    } catch (err) {
        console.error(`Error initializing theory animations: ${err.message}`);
    }
}

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', safeInitialize);

// Fallback initialization in case DOMContentLoaded already fired
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(safeInitialize, 100);
}

/**
 * Initialize particle animation for the hero section
 */
function initParticleAnimation() {
    const sketch = (p) => {
        const particles = [];
        const particleCount = 100;

        p.setup = () => {
            const container = document.getElementById('particles-js');
            const canvas = p.createCanvas(container.offsetWidth, container.offsetHeight);
            canvas.parent('particles-js');

            // Create particles
            for (let i = 0; i < particleCount; i++) {
                particles.push(new Particle(p));
            }
        };

        p.draw = () => {
            p.clear();

            // Update and display particles
            for (let i = 0; i < particles.length; i++) {
                particles[i].update();
                particles[i].display();

                // Connect nearby particles with lines
                for (let j = i + 1; j < particles.length; j++) {
                    const d = p.dist(
                        particles[i].position.x,
                        particles[i].position.y,
                        particles[j].position.x,
                        particles[j].position.y
                    );

                    if (d < 150) {
                        const alpha = p.map(d, 0, 150, 100, 0);
                        p.stroke(110, 68, 255, alpha);
                        p.line(
                            particles[i].position.x,
                            particles[i].position.y,
                            particles[j].position.x,
                            particles[j].position.y
                        );
                    }
                }
            }
        };

        p.windowResized = () => {
            const container = document.getElementById('particles-js');
            p.resizeCanvas(container.offsetWidth, container.offsetHeight);
        };

        class Particle {
            constructor(p) {
                this.p = p;
                this.position = p.createVector(p.random(p.width), p.random(p.height));
                this.velocity = p.createVector(p.random(-0.5, 0.5), p.random(-0.5, 0.5));
                this.size = p.random(3, 8);
                this.color = [
                    p.random([110, 255, 68]), // Primary, Secondary, or Accent color
                    p.random([68, 68, 255]),
                    p.random([255, 68, 221])
                ];
                this.alpha = p.random(100, 200);
            }

            update() {
                this.position.add(this.velocity);

                // Bounce off edges
                if (this.position.x < 0 || this.position.x > this.p.width) {
                    this.velocity.x *= -1;
                }
                if (this.position.y < 0 || this.position.y > this.p.height) {
                    this.velocity.y *= -1;
                }
            }

            display() {
                this.p.noStroke();
                this.p.fill(this.color[0], this.color[1], this.color[2], this.alpha);
                this.p.ellipse(this.position.x, this.position.y, this.size);
            }
        }
    };

    new p5(sketch);
}

/**
 * Initialize interactive diagrams based on type
 * @param {HTMLElement} element - The diagram container element
 * @param {string} type - The type of diagram to initialize
 */
function initDiagram(element, type) {
    // Create a canvas element with an ID that matches what the visualization modules expect
    const canvasId = `${type}-canvas`;

    // Clear any existing content
    element.innerHTML = '';

    // Set the element ID to match what the visualization modules expect
    element.id = canvasId;

    // Add UI elements needed for the visualizations
    addVisualizationUI(element, type);

    // Import the corresponding visualization module
    switch (type) {
        case 'mirror-reflection':
            initMirrorReflectionDiagram(element);
            break;
        case 'flow-of-co-creation':
            initFlowOfCoCreationDiagram(element);
            break;
        case 'vibrational-lensing':
            // Load the vibrational lensing visualization using a script tag approach
            loadVisualizationScript('vibrational-lensing', element);
            break;
        case 'energy-equivalence':
            // Load the energy equivalence visualization using a script tag approach
            loadVisualizationScript('energy-equivalence', element);
            break;
        case 'consciousness-kinetic':
            // Load the consciousness kinetic visualization using a script tag approach
            loadVisualizationScript('consciousness-kinetic', element);
            break;
        case 'time-dilation':
            // Load the time dilation visualization using a script tag approach
            loadVisualizationScript('time-dilation', element);
            break;
        case 'length-contraction':
            // Load the length contraction visualization using a script tag approach
            loadVisualizationScript('length-contraction', element);
            break;
        default:
            // Default placeholder animation
            initPlaceholderDiagram(element);
    }
}

/**
 * Add UI elements needed for the visualizations
 * @param {HTMLElement} element - The diagram container element
 * @param {string} type - The type of diagram to initialize
 */
function addVisualizationUI(element, type) {
    // Check if controls already exist for this element
    const existingControls = element.parentNode.querySelector('.visualization-controls');
    if (existingControls) {
        console.log('Controls already exist for this visualization, skipping UI creation');
        return;
    }

    // Create a container for UI controls
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'visualization-controls';
    controlsContainer.style.marginTop = '20px';
    controlsContainer.style.textAlign = 'center';

    // Add specific UI elements based on visualization type
    switch (type) {
        case 'energy-equivalence':
            // Add sliders for energy equivalence
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="m-np-slider">Nonphysical Mass (M_np): <span id="m-np-value">1.0</span></label>
                    <input type="range" id="m-np-slider" min="0.1" max="2" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="c-np-slider">Consciousness Current (C_np): <span id="c-np-value">1.0</span></label>
                    <input type="range" id="c-np-slider" min="0.1" max="2" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Energy (E_np): <span id="e-np-value">1.0 units</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'vibrational-lensing':
            // Add sliders for vibrational lensing
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="m-np-slider">Nonphysical Mass (M<sub>np</sub>): <span id="m-np-value">1.0</span></label>
                    <input type="range" id="m-np-slider" min="0.1" max="10" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="c-np-slider">Consciousness Current (C<sub>np</sub>): <span id="c-np-value">1.0</span></label>
                    <input type="range" id="c-np-slider" min="0.1" max="3" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Vibrational Lensing Angle: <span id="theta-np-value">1.00 rad</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'resistance-filter':
            // Add sliders for resistance filter
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="intention-slider">Intention Strength (X): <span id="intention-value">1.0</span></label>
                    <input type="range" id="intention-slider" min="0.1" max="5" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="resistance-slider">Resistance Level (R): <span id="resistance-value">0.50</span></label>
                    <input type="range" id="resistance-slider" min="0" max="1" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Outcome (Y): <span id="outcome-value">0.50</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'consciousness-kinetic':
            // Add sliders for consciousness kinetic
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="physical-slider">Physical Energy (E): <span id="physical-value">1.0</span></label>
                    <input type="range" id="physical-slider" min="0.1" max="5" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="nonphysical-slider">Nonphysical Energy (E<sub>np</sub>): <span id="nonphysical-value">1.0</span></label>
                    <input type="range" id="nonphysical-slider" min="0.1" max="5" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Consciousness-Kinetic Energy: <span id="kinetic-value">1.00 units</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'time-dilation':
            // Add sliders for time dilation
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="resistance-slider">Resistance Level (R): <span id="resistance-value">0.50</span></label>
                    <input type="range" id="resistance-slider" min="0" max="0.99" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="alignment-slider">Alignment Factor (A): <span id="alignment-value">0.50</span></label>
                    <input type="range" id="alignment-slider" min="0" max="0.99" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Time Dilation Factor (γ<sub>np</sub>): <span id="gamma-value">2.00</span></label>
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Subjective Time Experience: <span id="time-experience-value">2.00x longer</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'length-contraction':
            // Add sliders for length contraction
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="focus-slider">Focus Intensity (F): <span id="focus-value">0.5</span></label>
                    <input type="range" id="focus-slider" min="0" max="0.9" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="resonance-slider">Vibrational Resonance (R): <span id="resonance-value">0.5</span></label>
                    <input type="range" id="resonance-slider" min="0" max="1" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Focus Factor (γ): <span id="gamma-value">2.0</span></label>
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Contracted Distance: <span id="distance-value">50% of original</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;
    }

    // Add the controls container after the visualization
    element.parentNode.insertBefore(controlsContainer, element.nextSibling);
}

/**
 * Initialize mirror reflection diagram for Chapter 1
 * @param {HTMLElement} element - The diagram container element
 */
function initMirrorReflectionDiagram(element) {
    const sketch = (p) => {
        let centerX, centerY;
        let mirrorY;
        let particles = [];
        let reflections = [];

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            centerX = p.width / 2;
            centerY = p.height / 2;
            mirrorY = p.height * 0.6;

            // Create particles
            for (let i = 0; i < 30; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(mirrorY - 50),
                    size: p.random(5, 15),
                    speedX: p.random(-1, 1),
                    speedY: p.random(-1, 1),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                });

                // Create reflection for each particle
                reflections.push({
                    x: particles[i].x,
                    y: mirrorY + (mirrorY - particles[i].y),
                    size: particles[i].size,
                    color: p.color(p.red(particles[i].color), p.green(particles[i].color), p.blue(particles[i].color), 150)
                });
            }
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw mirror surface
            p.stroke(200, 200, 200, 100);
            p.strokeWeight(2);
            p.line(0, mirrorY, p.width, mirrorY);

            // Update and draw particles
            for (let i = 0; i < particles.length; i++) {
                // Update particle position
                particles[i].x += particles[i].speedX;
                particles[i].y += particles[i].speedY;

                // Bounce off edges
                if (particles[i].x < 0 || particles[i].x > p.width) {
                    particles[i].speedX *= -1;
                }
                if (particles[i].y < 0 || particles[i].y > mirrorY - particles[i].size / 2) {
                    particles[i].speedY *= -1;
                }

                // Update reflection
                reflections[i].x = particles[i].x;
                reflections[i].y = mirrorY + (mirrorY - particles[i].y);

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);

                // Draw reflection
                p.fill(reflections[i].color);
                p.ellipse(reflections[i].x, reflections[i].y, reflections[i].size);

                // Draw connection line
                p.stroke(255, 255, 255, 50);
                p.strokeWeight(0.5);
                p.line(particles[i].x, particles[i].y, reflections[i].x, reflections[i].y);
            }

            // Draw mirror surface highlight
            p.stroke(255, 255, 255, 150);
            p.strokeWeight(1);
            p.line(0, mirrorY, p.width, mirrorY);
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
            centerX = p.width / 2;
            centerY = p.height / 2;
            mirrorY = p.height * 0.6;
        };

        p.mousePressed = () => {
            if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
                // Add a new particle at mouse position if above mirror
                if (p.mouseY < mirrorY) {
                    const newParticle = {
                        x: p.mouseX,
                        y: p.mouseY,
                        size: p.random(5, 15),
                        speedX: p.random(-1, 1),
                        speedY: p.random(-1, 1),
                        color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                    };

                    particles.push(newParticle);

                    // Create reflection for the new particle
                    reflections.push({
                        x: newParticle.x,
                        y: mirrorY + (mirrorY - newParticle.y),
                        size: newParticle.size,
                        color: p.color(p.red(newParticle.color), p.green(newParticle.color), p.blue(newParticle.color), 150)
                    });

                    // Limit the number of particles
                    if (particles.length > 50) {
                        particles.shift();
                        reflections.shift();
                    }
                }
            }
        };
    };

    new p5(sketch);
}

/**
 * Initialize placeholder diagram for other chapters
 * @param {HTMLElement} element - The diagram container element
 */
function initPlaceholderDiagram(element) {
    const sketch = (p) => {
        let particles = [];

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create particles
            for (let i = 0; i < 50; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 10),
                    speedX: p.random(-1, 1),
                    speedY: p.random(-1, 1),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                });
            }
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Update and draw particles
            for (let i = 0; i < particles.length; i++) {
                // Update particle position
                particles[i].x += particles[i].speedX;
                particles[i].y += particles[i].speedY;

                // Bounce off edges
                if (particles[i].x < 0 || particles[i].x > p.width) {
                    particles[i].speedX *= -1;
                }
                if (particles[i].y < 0 || particles[i].y > p.height) {
                    particles[i].speedY *= -1;
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);
            }
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };
    };

    new p5(sketch);
}

/**
 * Initialize reading progress bar
 */
function initReadingProgress() {
    const progressBar = document.querySelector('.progress-bar');
    if (!progressBar) return;

    window.addEventListener('scroll', () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;

        const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
        progressBar.style.width = `${scrollPercentage}%`;
    });
}

/**
 * Initialize reading settings
 */
function initReadingSettings() {
    const settingsToggle = document.querySelector('.settings-toggle');
    const settingsPanel = document.querySelector('.settings-panel');
    const fontSizeButtons = document.querySelectorAll('.font-size-btn');
    const fontSizeValue = document.querySelector('.font-size-value');
    const themeOptions = document.querySelectorAll('.theme-option');
    const readingModeOptions = document.querySelectorAll('.reading-mode-option input');

    if (!settingsToggle || !settingsPanel) return;

    // Toggle settings panel
    settingsToggle.addEventListener('click', () => {
        settingsPanel.classList.toggle('active');
    });

    // Font size controls
    if (fontSizeButtons.length && fontSizeValue) {
        let fontSize = 100;

        fontSizeButtons.forEach(button => {
            button.addEventListener('click', () => {
                if (button.classList.contains('increase-font')) {
                    fontSize = Math.min(fontSize + 10, 150);
                } else {
                    fontSize = Math.max(fontSize - 10, 70);
                }

                fontSizeValue.textContent = `${fontSize}%`;
                document.querySelector('.chapter-content').style.fontSize = `${fontSize}%`;
            });
        });
    }

    // Theme options
    if (themeOptions.length) {
        themeOptions.forEach(option => {
            option.addEventListener('click', () => {
                // Remove active class from all options
                themeOptions.forEach(opt => opt.classList.remove('active'));

                // Add active class to clicked option
                option.classList.add('active');

                // Apply theme
                const body = document.querySelector('.theory-page');
                body.classList.remove('light-theme', 'sepia-theme');

                if (option.classList.contains('light')) {
                    body.classList.add('light-theme');
                } else if (option.classList.contains('sepia')) {
                    body.classList.add('sepia-theme');
                }
            });
        });
    }

    // Reading mode options
    if (readingModeOptions.length) {
        readingModeOptions.forEach(option => {
            option.addEventListener('change', () => {
                const body = document.querySelector('.theory-page');
                body.classList.remove('focus-mode', 'column-mode');

                if (option.value === 'focus') {
                    body.classList.add('focus-mode');
                } else if (option.value === 'column') {
                    body.classList.add('column-mode');
                }
            });
        });
    }
}

/**
 * Initialize bookmarks
 */
function initBookmarks() {
    const bookmarkButton = document.querySelector('.bookmark-button');
    const bookmarkPanel = document.querySelector('.bookmark-panel');
    const bookmarkForm = document.querySelector('.bookmark-form');
    const bookmarkList = document.querySelector('.bookmark-list');

    if (!bookmarkButton || !bookmarkPanel || !bookmarkForm || !bookmarkList) return;

    // Toggle bookmark panel
    bookmarkButton.addEventListener('click', () => {
        bookmarkPanel.classList.toggle('active');
        bookmarkButton.classList.toggle('active');
    });

    // Add bookmark
    bookmarkForm.addEventListener('submit', (e) => {
        e.preventDefault();

        const textarea = bookmarkForm.querySelector('textarea');
        const note = textarea.value.trim();

        if (note) {
            const scrollPosition = window.scrollY;
            const timestamp = new Date().toLocaleTimeString();

            // Create bookmark element
            const bookmark = document.createElement('div');
            bookmark.classList.add('bookmark-item');
            bookmark.innerHTML = `
                <div class="bookmark-header">
                    <span class="bookmark-time">${timestamp}</span>
                    <button class="bookmark-delete">×</button>
                </div>
                <div class="bookmark-note">${note}</div>
            `;

            // Add click event to scroll to bookmark position
            bookmark.addEventListener('click', () => {
                window.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth'
                });
            });

            // Add click event to delete button
            const deleteButton = bookmark.querySelector('.bookmark-delete');
            deleteButton.addEventListener('click', (e) => {
                e.stopPropagation();
                bookmark.remove();
            });

            // Add bookmark to list
            bookmarkList.appendChild(bookmark);

            // Clear textarea
            textarea.value = '';
        }
    });
}

/**
 * Initialize highlight tooltips
 */
function initHighlightTooltips() {
    const highlights = document.querySelectorAll('.highlight');

    if (!highlights.length) return;

    highlights.forEach(highlight => {
        // Create tooltip element
        const tooltip = document.createElement('div');
        tooltip.classList.add('highlight-tooltip');
        tooltip.textContent = highlight.getAttribute('data-note');

        // Add tooltip to highlight
        highlight.appendChild(tooltip);

        // Show tooltip on hover
        highlight.addEventListener('mouseenter', () => {
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
        });

        // Hide tooltip on mouse leave
        highlight.addEventListener('mouseleave', () => {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
        });
    });
}

/**
 * Add a tooltip to explain interactive elements on first visit
 */
function addInteractiveElementsTooltip() {
    // Check if this is the first visit
    if (localStorage.getItem('tsr_visited')) return;

    // Mark as visited
    localStorage.setItem('tsr_visited', 'true');

    // Create and add the tooltip
    const tooltip = document.createElement('div');
    tooltip.classList.add('interactive-elements-tooltip');
    tooltip.innerHTML = `
        <div class="tooltip-header">
            <h3>Interactive Elements</h3>
            <button class="tooltip-close">×</button>
        </div>
        <div class="tooltip-content">
            <p>This document contains several interactive elements:</p>
            <ul>
                <li><strong>Highlighted text</strong> - Hover or click for additional information</li>
                <li><strong>Interactive diagrams</strong> - Click or drag to interact with visualizations</li>
                <li><strong>Reading settings</strong> - Customize your reading experience</li>
                <li><strong>Bookmarks</strong> - Save sections with notes for later reference</li>
            </ul>
        </div>
    `;

    // Style the tooltip
    tooltip.style.position = 'fixed';
    tooltip.style.bottom = '20px';
    tooltip.style.right = '20px';
    tooltip.style.width = '300px';
    tooltip.style.backgroundColor = 'var(--bg-color-light)';
    tooltip.style.borderRadius = '10px';
    tooltip.style.boxShadow = '0 5px 20px rgba(0, 0, 0, 0.3)';
    tooltip.style.zIndex = '1000';
    tooltip.style.border = '1px solid var(--border-color)';
    tooltip.style.animation = 'fadeInUp 0.5s ease 1s forwards';
    tooltip.style.opacity = '0';

    // Style the tooltip header
    const header = tooltip.querySelector('.tooltip-header');
    if (header) {
        header.style.display = 'flex';
        header.style.justifyContent = 'space-between';
        header.style.alignItems = 'center';
        header.style.padding = '10px 15px';
        header.style.borderBottom = '1px solid var(--border-color)';
    }

    // Style the tooltip content
    const content = tooltip.querySelector('.tooltip-content');
    if (content) {
        content.style.padding = '15px';
    }

    // Style the close button
    const closeBtn = tooltip.querySelector('.tooltip-close');
    if (closeBtn) {
        closeBtn.style.background = 'none';
        closeBtn.style.border = 'none';
        closeBtn.style.fontSize = '20px';
        closeBtn.style.cursor = 'pointer';
        closeBtn.style.color = 'var(--text-color)';

        // Add click event to close the tooltip
        closeBtn.addEventListener('click', () => {
            tooltip.style.animation = 'fadeOutDown 0.5s ease forwards';
            setTimeout(() => {
                tooltip.remove();
            }, 500);
        });
    }

    // Add the tooltip to the body
    document.body.appendChild(tooltip);

    // Auto-remove after 15 seconds
    setTimeout(() => {
        if (document.body.contains(tooltip)) {
            tooltip.style.animation = 'fadeOutDown 0.5s ease forwards';
            setTimeout(() => {
                if (document.body.contains(tooltip)) {
                    tooltip.remove();
                }
            }, 500);
        }
    }, 15000);
}

// Add the necessary animations to the document
function addTooltipAnimations() {
    // Check if the animations already exist
    if (document.getElementById('tooltip-animations')) return;

    const style = document.createElement('style');
    style.id = 'tooltip-animations';
    style.textContent = `
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes fadeOutDown {
            from {
                opacity: 1;
                transform: translateY(0);
            }
            to {
                opacity: 0;
                transform: translateY(20px);
            }
        }
        
        .interactive-elements-tooltip {
            font-family: var(--font-primary, sans-serif);
            color: var(--text-color, #e0e0e0);
            font-size: 14px;
            line-height: 1.5;
        }
        
        .interactive-elements-tooltip h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
        }
        
        .interactive-elements-tooltip p {
            margin-top: 0;
        }
        
        .interactive-elements-tooltip ul {
            margin-bottom: 0;
            padding-left: 20px;
        }
        
        .interactive-elements-tooltip li {
            margin-bottom: 5px;
        }
    `;

    document.head.appendChild(style);
}

// Call the function to add animations
addTooltipAnimations();

/**
 * Load a simplified visualization for chapter pages
 * This avoids the parcelRequire issues with dynamic imports
 * @param {string} type - The type of visualization to load
 * @param {HTMLElement} element - The container element
 */
function loadVisualizationScript(type, element) {
    console.log(`Loading ${type} visualization for chapter page`);

    // Instead of dynamically loading the full visualization scripts,
    // we'll create simplified versions directly here
    switch (type) {
        case 'energy-equivalence':
            initSimplifiedEnergyEquivalence(element);
            break;
        case 'vibrational-lensing':
            initSimplifiedVibrationalLensing(element);
            break;
        case 'consciousness-kinetic':
            initSimplifiedConsciousnessKinetic(element);
            break;
        case 'time-dilation':
            initSimplifiedTimeDilation(element);
            break;
        case 'length-contraction':
            initSimplifiedLengthContraction(element);
            break;
        case 'resistance-filter':
            initSimplifiedResistanceFilter(element);
            break;
        default:
            // Default placeholder animation
            initPlaceholderDiagram(element);
    }
}

/**
 * Initialize a simplified energy equivalence visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedEnergyEquivalence(element) {
    const sketch = (p) => {
        let particles = [];
        let time = 0;
        let mNp = 1.0;
        let cNp = 1.0;
        let showComparison = false;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create particles
            for (let i = 0; i < 50; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 8),
                    speed: p.random(0.5, 2),
                    angle: p.random(p.TWO_PI),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                });
            }

            // Add event listeners to sliders
            setupEventListeners();
        };

        // Setup event listeners for the sliders and buttons
        function setupEventListeners() {
            // Get the slider elements
            const mNpSlider = document.getElementById('m-np-slider');
            const cNpSlider = document.getElementById('c-np-slider');
            const resetButton = document.getElementById('reset-button');
            const toggleButton = document.getElementById('toggle-comparison');

            if (mNpSlider) {
                mNpSlider.addEventListener('input', function () {
                    mNp = parseFloat(this.value);
                    document.getElementById('m-np-value').textContent = mNp.toFixed(1);
                    updateEnergyValue();
                });
            }

            if (cNpSlider) {
                cNpSlider.addEventListener('input', function () {
                    cNp = parseFloat(this.value);
                    document.getElementById('c-np-value').textContent = cNp.toFixed(1);
                    updateEnergyValue();
                });
            }

            if (resetButton) {
                resetButton.addEventListener('click', function () {
                    mNp = 1.0;
                    cNp = 1.0;
                    if (mNpSlider) mNpSlider.value = mNp;
                    if (cNpSlider) cNpSlider.value = cNp;
                    document.getElementById('m-np-value').textContent = mNp.toFixed(1);
                    document.getElementById('c-np-value').textContent = cNp.toFixed(1);
                    updateEnergyValue();
                });
            }

            if (toggleButton) {
                toggleButton.addEventListener('click', function () {
                    showComparison = !showComparison;
                    this.textContent = showComparison ? 'Hide Comparison' : 'Show Comparison';
                });
            }

            // Initial update
            updateEnergyValue();
        }

        // Update the energy value display
        function updateEnergyValue() {
            const eNp = mNp * cNp * cNp;
            const eNpElement = document.getElementById('e-np-value');
            if (eNpElement) {
                eNpElement.textContent = eNp.toFixed(2) + ' units';
            }
        }

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw energy field
            p.noFill();
            p.stroke(241, 196, 15, 100);
            p.strokeWeight(2);
            p.ellipse(p.width / 2, p.height / 2, 150 + Math.sin(time) * 20);

            // Update and draw particles
            for (let i = 0; i < particles.length; i++) {
                // Update particle position
                particles[i].x += Math.cos(particles[i].angle) * particles[i].speed;
                particles[i].y += Math.sin(particles[i].angle) * particles[i].speed;

                // Bounce off edges
                if (particles[i].x < 0 || particles[i].x > p.width) {
                    particles[i].angle = Math.PI - particles[i].angle;
                }
                if (particles[i].y < 0 || particles[i].y > p.height) {
                    particles[i].angle = -particles[i].angle;
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);
            }

            // Update time
            time += 0.05;
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };
    };

    new p5(sketch);
}

/**
 * Initialize a simplified vibrational lensing visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedVibrationalLensing(element) {
    const sketch = (p) => {
        let waves = [];
        let time = 0;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create waves
            for (let i = 0; i < 3; i++) {
                waves.push({
                    amplitude: p.random(10, 30),
                    frequency: p.random(0.02, 0.05),
                    phase: p.random(p.TWO_PI),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), 150)
                });
            }
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw waves
            const centerY = p.height / 2;

            for (let wave of waves) {
                p.stroke(wave.color);
                p.strokeWeight(2);
                p.noFill();

                p.beginShape();
                for (let x = 0; x < p.width; x += 5) {
                    const y = centerY + Math.sin(x * wave.frequency + time + wave.phase) * wave.amplitude;
                    p.vertex(x, y);
                }
                p.endShape();
            }

            // Draw lens
            p.noStroke();
            p.fill(142, 68, 173, 100);
            p.rect(p.width / 2 - 10, p.height / 2 - 100, 20, 200);

            // Update time
            time += 0.05;
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };
    };

    new p5(sketch);
}

/**
 * Initialize a simplified consciousness kinetic visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedConsciousnessKinetic(element) {
    const sketch = (p) => {
        let particles = [];
        let time = 0;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create particles
            for (let i = 0; i < 30; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 8),
                    speed: p.random(0.5, 2),
                    angle: p.random(p.TWO_PI),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                });
            }
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw energy field
            const centerX = p.width / 2;
            const centerY = p.height / 2;

            p.noFill();
            for (let i = 0; i < 3; i++) {
                p.stroke(241, 196, 15, 150 - i * 30);
                p.strokeWeight(2);
                p.ellipse(centerX, centerY, 100 + i * 20 + Math.sin(time) * 10);
            }

            // Update and draw particles
            for (let i = 0; i < particles.length; i++) {
                // Calculate direction to center
                const dx = centerX - particles[i].x;
                const dy = centerY - particles[i].y;
                const angle = Math.atan2(dy, dx);

                // Adjust particle angle slightly towards center
                particles[i].angle = p.lerp(particles[i].angle, angle, 0.03);

                // Update particle position
                particles[i].x += Math.cos(particles[i].angle) * particles[i].speed;
                particles[i].y += Math.sin(particles[i].angle) * particles[i].speed;

                // Bounce off edges
                if (particles[i].x < 0 || particles[i].x > p.width) {
                    particles[i].angle = Math.PI - particles[i].angle;
                }
                if (particles[i].y < 0 || particles[i].y > p.height) {
                    particles[i].angle = -particles[i].angle;
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);

                // Draw trail
                p.stroke(particles[i].color);
                p.strokeWeight(1);
                p.line(
                    particles[i].x,
                    particles[i].y,
                    particles[i].x - Math.cos(particles[i].angle) * particles[i].speed * 5,
                    particles[i].y - Math.sin(particles[i].angle) * particles[i].speed * 5
                );
            }

            // Update time
            time += 0.05;
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };
    };

    new p5(sketch);
}

/**
 * Initialize a simplified time dilation visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedTimeDilation(element) {
    const sketch = (p) => {
        let time = 0;
        let normalClock, dilatedClock;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            normalClock = {
                x: p.width * 0.3,
                y: p.height / 2,
                radius: 60,
                speed: 1
            };

            dilatedClock = {
                x: p.width * 0.7,
                y: p.height / 2,
                radius: 60,
                speed: 0.5
            };
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw clocks
            drawClock(normalClock, time * normalClock.speed, p.color(231, 76, 60));
            drawClock(dilatedClock, time * dilatedClock.speed, p.color(46, 204, 113));

            // Draw connecting line
            p.stroke(241, 196, 15);
            p.strokeWeight(2);
            p.line(normalClock.x, normalClock.y, dilatedClock.x, dilatedClock.y);

            // Update time
            time += 0.02;
        };

        function drawClock(clock, clockTime, color) {
            // Draw clock face
            p.noFill();
            p.stroke(color);
            p.strokeWeight(3);
            p.ellipse(clock.x, clock.y, clock.radius * 2);

            // Draw hour marks
            for (let i = 0; i < 12; i++) {
                const angle = i * p.TWO_PI / 12;
                const innerRadius = clock.radius * 0.9;
                const outerRadius = clock.radius;
                const x1 = clock.x + Math.cos(angle) * innerRadius;
                const y1 = clock.y + Math.sin(angle) * innerRadius;
                const x2 = clock.x + Math.cos(angle) * outerRadius;
                const y2 = clock.y + Math.sin(angle) * outerRadius;
                p.stroke(200);
                p.strokeWeight(2);
                p.line(x1, y1, x2, y2);
            }

            // Draw second hand
            const secondAngle = (clockTime * 6) % p.TWO_PI;
            const secondX = clock.x + Math.cos(secondAngle - p.HALF_PI) * clock.radius * 0.9;
            const secondY = clock.y + Math.sin(secondAngle - p.HALF_PI) * clock.radius * 0.9;
            p.stroke(color);
            p.strokeWeight(1);
            p.line(clock.x, clock.y, secondX, secondY);

            // Draw minute hand
            const minuteAngle = (clockTime / 60) % p.TWO_PI;
            const minuteX = clock.x + Math.cos(minuteAngle - p.HALF_PI) * clock.radius * 0.7;
            const minuteY = clock.y + Math.sin(minuteAngle - p.HALF_PI) * clock.radius * 0.7;
            p.stroke(200);
            p.strokeWeight(2);
            p.line(clock.x, clock.y, minuteX, minuteY);

            // Draw center dot
            p.fill(200);
            p.noStroke();
            p.ellipse(clock.x, clock.y, 5);
        }

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);

            normalClock.x = p.width * 0.3;
            normalClock.y = p.height / 2;

            dilatedClock.x = p.width * 0.7;
            dilatedClock.y = p.height / 2;
        };
    };

    new p5(sketch);
}

/**
 * Initialize a simplified length contraction visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedLengthContraction(element) {
    const sketch = (p) => {
        let time = 0;
        let objects = [];

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create objects
            objects = [
                {
                    x: p.width * 0.3,
                    y: p.height / 2,
                    width: 100,
                    height: 40,
                    color: p.color(231, 76, 60),
                    contracted: false
                },
                {
                    x: p.width * 0.7,
                    y: p.height / 2,
                    width: 100,
                    height: 40,
                    color: p.color(46, 204, 113),
                    contracted: true
                }
            ];
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw objects
            for (let obj of objects) {
                // Calculate contraction
                let displayWidth = obj.width;
                if (obj.contracted) {
                    displayWidth = obj.width * (0.5 + 0.2 * Math.sin(time));
                }

                // Draw object
                p.fill(obj.color);
                p.noStroke();
                p.rect(obj.x - displayWidth / 2, obj.y - obj.height / 2, displayWidth, obj.height);

                // Draw label
                p.fill(255);
                p.textAlign(p.CENTER, p.CENTER);
                p.textSize(14);
                p.text(obj.contracted ? "Contracted" : "Normal", obj.x, obj.y + obj.height + 20);
            }

            // Draw connecting line
            p.stroke(241, 196, 15);
            p.strokeWeight(2);
            p.line(objects[0].x, objects[0].y, objects[1].x, objects[1].y);

            // Update time
            time += 0.05;
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);

            objects[0].x = p.width * 0.3;
            objects[0].y = p.height / 2;

            objects[1].x = p.width * 0.7;
            objects[1].y = p.height / 2;
        };
    };

    new p5(sketch);
}

/**
 * Initialize a simplified resistance filter visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedResistanceFilter(element) {
    const sketch = (p) => {
        let particles = [];
        let filterY;
        let time = 0;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            filterY = p.height / 2;

            // Create particles
            for (let i = 0; i < 50; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 8),
                    speed: p.random(0.5, 2),
                    angle: p.random(p.TWO_PI),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200)),
                    blocked: false
                });
            }
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw filter
            const filterHeight = 20;
            const filterWidth = p.width * 0.8;
            const filterX = p.width / 2 - filterWidth / 2;

            p.noStroke();
            p.fill(142, 68, 173, 150);
            p.rect(filterX, filterY - filterHeight / 2, filterWidth, filterHeight);

            p.stroke(142, 68, 173);
            p.strokeWeight(2);
            p.noFill();
            p.rect(filterX, filterY - filterHeight / 2, filterWidth, filterHeight);

            // Update and draw particles
            for (let i = 0; i < particles.length; i++) {
                // Update position
                if (!particles[i].blocked) {
                    particles[i].x += Math.cos(particles[i].angle) * particles[i].speed;
                    particles[i].y += Math.sin(particles[i].angle) * particles[i].speed;
                } else {
                    particles[i].x += (Math.random() - 0.5) * 0.5;
                    particles[i].y += (Math.random() - 0.5) * 0.5;
                }

                // Wrap around edges
                if (particles[i].x < 0) particles[i].x = p.width;
                if (particles[i].x > p.width) particles[i].x = 0;
                if (particles[i].y < 0) particles[i].y = p.height;
                if (particles[i].y > p.height) particles[i].y = 0;

                // Check if particle is passing through filter
                if (Math.abs(particles[i].y - filterY) < filterHeight / 2 &&
                    particles[i].x > filterX && particles[i].x < filterX + filterWidth) {

                    // 50% chance of being blocked
                    if (Math.random() < 0.5) {
                        particles[i].blocked = true;
                        particles[i].color = p.color(231, 76, 60);
                        particles[i].angle = p.random(p.TWO_PI);
                    } else {
                        particles[i].blocked = false;
                        particles[i].color = p.color(46, 204, 113);
                    }
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);
            }

            // Update time
            time += 0.05;
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
            filterY = p.height / 2;
        };
    };

    new p5(sketch);
}
